import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { Subject, finalize, takeUntil } from 'rxjs';
import { ShiftReportData, ShiftReportEmployee } from '../../models/shift-report.model';
import { ReportsService } from '../../reports.service';
import { InfoTipComponent } from '../../../shared/components/info-tip';
import { SHIFT_REPORT_TIPS } from '../../report-field-definitions';

@Component({
  selector: 'app-shift-report-results',
  standalone: true,
  imports: [CommonModule, InfoTipComponent],
  templateUrl: './shift-report-results.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './shift-report-results.component.scss',
})
export class ShiftReportResultsComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input({ required: true }) report!: ShiftReportData;

  readonly tips = SHIFT_REPORT_TIPS;
  @Input({ required: true }) date!: string;
  @Input() locationIds: string[] = [];
  @Input() employeeIds: string[] = [];

  @ViewChild('tabsScroll') tabsScroll?: ElementRef<HTMLElement>;

  activeEmployeeId = '';
  canScrollLeft = false;
  canScrollRight = false;
  exportingExcel = false;
  exportingPdf = false;

  private readonly reportsService = inject(ReportsService);
  private readonly messageService = inject(NotificationService);
  private readonly destroy$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['report']) {
      return;
    }
    const employees = this.report?.employees ?? [];
    const stillValid = employees.some(emp => emp.employee_id === this.activeEmployeeId);
    if (!stillValid) {
      this.activeEmployeeId = employees[0]?.employee_id ?? '';
    }
    queueMicrotask(() => this.updateTabsScrollState());
  }

  ngAfterViewInit(): void {
    const el = this.tabsScroll?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') {
      this.updateTabsScrollState();
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.updateTabsScrollState());
    this.resizeObserver.observe(el);
    this.updateTabsScrollState();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get activeEmployee(): ShiftReportEmployee | null {
    return this.report?.employees?.find(emp => emp.employee_id === this.activeEmployeeId) ?? null;
  }

  get maxPackageCount(): number {
    const packages = this.activeEmployee?.packages ?? [];
    if (!packages.length) {
      return 1;
    }
    return Math.max(...packages.map(pkg => pkg.count), 1);
  }

  selectEmployee(employeeId: string): void {
    this.activeEmployeeId = employeeId;
  }

  onTabsScroll(): void {
    this.updateTabsScrollState();
  }

  scrollTabs(direction: 'left' | 'right'): void {
    const el = this.tabsScroll?.nativeElement;
    if (!el) {
      return;
    }
    const delta = Math.max(el.clientWidth * 0.55, 160);
    el.scrollBy({ left: direction === 'right' ? delta : -delta, behavior: 'smooth' });
    window.setTimeout(() => this.updateTabsScrollState(), 280);
  }

  exportExcel(): void {
    this.downloadExport('xlsx');
  }

  exportPdf(): void {
    this.downloadExport('pdf');
  }

  private downloadExport(format: 'pdf' | 'xlsx'): void {
    if (this.exportingPdf || this.exportingExcel || !this.date) {
      return;
    }
    if (!this.report?.employees?.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Nothing to export',
        detail: 'Generate a shift report with data before exporting.',
      });
      return;
    }

    if (format === 'pdf') {
      this.exportingPdf = true;
    } else {
      this.exportingExcel = true;
    }

    const body = {
      date: this.date,
      location_ids: this.locationIds?.length ? this.locationIds : undefined,
      employee_ids: this.employeeIds?.length ? this.employeeIds : undefined,
    };

    const request$ =
      format === 'pdf'
        ? this.reportsService.downloadShiftReportPdf(body)
        : this.reportsService.downloadShiftReportXlsx(body);

    const ext = format === 'pdf' ? 'pdf' : 'xlsx';
    const label = format === 'pdf' ? 'PDF' : 'Excel';

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.exportingPdf = false;
          this.exportingExcel = false;
        })
      )
      .subscribe({
        next: blob => {
          const filename = `shift-report-${this.date}.${ext}`;
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
          this.messageService.add({
            severity: 'success',
            summary: 'Export ready',
            detail: `${label} file downloaded.`,
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Export failed',
            detail: `Unable to download the ${label} report. Please try again.`,
          });
        },
      });
  }

  formatMoney(value: number | null | undefined): string {
    const amount = Number(value ?? 0);
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  packageBarWidth(count: number): string {
    return `${Math.max((count / this.maxPackageCount) * 100, 4)}%`;
  }

  trackByEmployeeId(_index: number, emp: ShiftReportEmployee): string {
    return emp.employee_id;
  }

  private updateTabsScrollState(): void {
    const el = this.tabsScroll?.nativeElement;
    if (!el) {
      this.canScrollLeft = false;
      this.canScrollRight = false;
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    this.canScrollLeft = el.scrollLeft > 2;
    this.canScrollRight = maxScroll > 2 && el.scrollLeft < maxScroll - 2;
  }
}
